import { useCallback, useEffect } from 'react';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useFormikContext } from 'formik';

import { AsyncListWithSearch, StickyWrapper } from '~components/atoms';
import { useQueryInfiniteVenues } from '~hooks';
import {
  PINNING_STEP,
  usePinningStepContext,
  useStartAndFindVenueContext,
} from '~providers';
import { IFormCreationValues } from '~providers/useForm';

export function AsyncListWithSearchForVenue() {
  const { goToNextStep, handleSetSelectedVenueId, handleSetPinningStep } =
    usePinningStepContext();

  const { setFieldValue } = useFormikContext<IFormCreationValues>();

  const { items, onSearch, fetchNextPage, hasNextPage } =
    useQueryInfiniteVenues();

  const {
    venueName,
    handleSetIsTyping,
    optionsOpen,
    handleSetInitialFocus,
    onInputValueChange,
    isVenueNotExisted,
    enabledVenueCreation,
  } = useStartAndFindVenueContext();

  const onVenueFilterOptionClick = useCallback(
    (id: string) => {
      handleSetSelectedVenueId(id);
      handleSetPinningStep(PINNING_STEP.CONFIRM_VENUE);
    },
    [handleSetPinningStep, handleSetSelectedVenueId],
  );

  const handleGoToNextStep = () => {
    handleSetSelectedVenueId(undefined);
    setFieldValue('venue.locationName', venueName);
    goToNextStep();
  };

  useEffect(() => {
    handleSetIsTyping(false);
  }, [handleSetIsTyping, items]);

  return (
    <>
      <AsyncListWithSearch
        onSearching={onSearch}
        optionsOpen={optionsOpen && !isVenueNotExisted(items.length)}
        onInputValueChange={onInputValueChange}
        dataLength={items.length}
        next={fetchNextPage}
        hasMore={!!hasNextPage}
        loader={<CircularProgress />}
        textFieldProps={{ onFocus: handleSetInitialFocus }}
      >
        {/* Map over characters array and return JSX */}
        {items.map((venue) => (
          <Stack
            paddingY={1.5}
            key={venue.id}
            onClick={() => onVenueFilterOptionClick(venue.id)}
          >
            <Typography color="text.dark" fontSize="16px">
              {venue.name}
            </Typography>
            <Typography color="text.placeholder" fontSize="14px">
              {venue.addressLine1}
            </Typography>
          </Stack>
        ))}
      </AsyncListWithSearch>

      <StickyWrapper visible={enabledVenueCreation()}>
        <Button
          fullWidth
          size="large"
          variant={items.length ? 'outlined' : 'contained'}
          onClick={handleGoToNextStep}
        >
          <Typography variant="body2" fontWeight={400}>
            Create New Venue
          </Typography>
        </Button>
      </StickyWrapper>

      {isVenueNotExisted(items.length) && (
        <Stack>
          <Typography variant="body2" color="#6B7280">
            Unable to find “{venueName}”. Try a different search term or create
            a new venue.
          </Typography>
        </Stack>
      )}
    </>
  );
}
